import { useCallback, useEffect } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router';

import { cn } from '@/utils/classname';

import { UnsavedChangesModal } from './components/Modals/UnsavedChangesModal';
import { QueryBuilderSidebarWrapper } from './components/QueryBuilderSidebar';
import { QueryBuilderWorkstation } from './components/QueryBuilderWorkstation';
import {
  QueryDataProvider,
  QueryUIProvider,
  useQueryBuilder,
} from './state/useQueryBuilder';
import { shouldAllowDirtyQueryBuilderNavigation } from './utils/navigationBlocker';

/**
 * QueryBuilder - Graph query construction and execution interface
 *
 * Layout:
 * - Sidebar (collapsible): Folder navigation with 7 query categories
 * - Workstation: Query editing and results display
 *
 * State Management:
 * - Split context architecture for optimal performance
 * - QueryDataContext: queryState (flatGraph + metadata) + mutations
 * - QueryUIContext: mode, sidebar, maximize state, isGlobal flag
 * - Components subscribe only to needed context
 *
 * Workstation Modes:
 * - query: Execute queries and view results (default)
 * - edit: Edit query structure
 * - view: View-only mode for query inspection
 *
 * Global Mode:
 * - When isGlobal={true}, enables user selector dropdown for collaborative queries
 * - Used in Customer Management for global query builder
 * - Defaults to false for regular Insights usage
 */
const QueryBuilderContent = () => {
  const { isMaximized, queryState } = useQueryBuilder();
  const isDirty = queryState?.isDirty ?? false;

  useBeforeUnload(
    useCallback(
      event => {
        if (!isDirty) return;
        event.preventDefault();
        event.returnValue = '';
      },
      [isDirty]
    )
  );

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const shouldAllow = shouldAllowDirtyQueryBuilderNavigation({
      isDirty,
      currentPathname: currentLocation.pathname,
      nextPathname: nextLocation.pathname,
      currentSearch: currentLocation.search,
      nextSearch: nextLocation.search,
    });
    return !shouldAllow;
  });
  const shouldShowPrompt = blocker.state === 'blocked';

  useEffect(() => {
    if (!isDirty && blocker.state === 'blocked') {
      blocker.reset?.();
    }
  }, [blocker, isDirty]);

  return (
    <>
      <div
        className={cn(
          'relative flex h-full gap-2 transition-all duration-200',
          isMaximized
            ? 'bg-chariot-background-primary fixed inset-0 left-[58px] z-20 ml-10 max-w-screen overflow-hidden pr-12'
            : 'w-full'
        )}
        data-testid="query-builder-root"
        data-tutorial="query-builder"
      >
        <QueryBuilderSidebarWrapper />
        <div
          className={cn('flex-1 overflow-hidden transition-all duration-200')}
        >
          <QueryBuilderWorkstation />
        </div>
      </div>
      <UnsavedChangesModal
        isOpen={shouldShowPrompt}
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </>
  );
};

export interface QueryBuilderProps {
  isGlobal?: boolean;
}

export const QueryBuilder = ({ isGlobal = false }: QueryBuilderProps) => {
  return (
    <QueryDataProvider>
      <QueryUIProvider initialIsGlobal={isGlobal}>
        <QueryBuilderContent />
      </QueryUIProvider>
    </QueryDataProvider>
  );
};
